import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatDate, formatEUR, PROSPECT_STATUSES, ROLE_LABELS } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, SelectField, TextArea, Section } from "@/components/ui-kit";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Save, MessageSquare, History as HistoryIcon, Trash2, FileSignature, Sparkles, Mail, Send, Phone, RefreshCw, CalendarPlus } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prospect, setProspect] = useState(null);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [tab, setTab] = useState("comments");
  const [sendingEmail, setSendingEmail] = useState("");
  const [relancing, setRelancing] = useState("");

  const canEdit = ["admin", "dirigeant", "manager_commercial", "vendeur"].includes(user?.role);
  const canDelete = ["admin", "dirigeant", "manager_commercial"].includes(user?.role);

  const sendClientEmail = async (kind) => {
    const label = kind === "presentation" ? "de présentation" : "de remerciement";
    if (!window.confirm(`Envoyer l'email ${label} à ${prospect.prenom} ${prospect.nom} (${prospect.email}) ?`)) return;
    setSendingEmail(kind);
    try {
      const { data } = await api.post(`/prospects/${id}/send-client-email`, { kind });
      if (data.redirected) {
        toast.success(`Email ${label} envoyé (mode test → votre boîte Yahoo).`, { duration: 6000 });
      } else {
        toast.success(`Email ${label} envoyé à ${data.recipient}.`, { duration: 6000 });
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi de l'email.");
    } finally {
      setSendingEmail("");
    }
  };

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/prospects/${id}/rdv.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rdv-${(prospect.nom || "client")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Événement téléchargé — ouvre-le pour l'ajouter à ton agenda 📅", { duration: 5000 });
    } catch (e) {
      toast.error("Renseigne d'abord une date de RDV, puis enregistre la fiche.");
    }
  };

  const doRelance = async (canal, send_email) => {
    setRelancing(canal);
    try {
      const { data } = await api.post(`/prospects/${id}/relance`, { canal, send_email });
      if (data.email_sent) toast.success("Relance envoyée par email 📧 (mode test → boîte interne).", { duration: 5000 });
      else if (data.terminee) toast.success("R3 atteint — relance bouclée. Pense à conclure ou mettre en veille.", { duration: 5000 });
      else toast.success(`Relance notée 💪 Prochaine relance le ${data.prochaine_date}.`, { duration: 5000 });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    } finally {
      setRelancing("");
    }
  };

  const loadAll = () => {
    api.get(`/prospects/${id}`).then((r) => setProspect(r.data)).catch(() => navigate("/prospects"));
    api.get(`/prospects/${id}/comments`).then((r) => setComments(r.data));
    api.get(`/history/prospect/${id}`).then((r) => setHistory(r.data));
  };

  useEffect(() => {
    loadAll();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!prospect) return <div className="p-8">Chargement…</div>;

  const set = (k, v) => setProspect((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...prospect };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by;
      if (payload.prix) payload.prix = parseFloat(payload.prix);
      const oldStatut = prospect.statut;
      await api.put(`/prospects/${id}`, payload);
      toast.success("Prospect mis à jour");
      loadAll();
      if (payload.statut === "signe" && oldStatut !== "signe") {
        toast.success("Vente signée ! Dossier créé dans le BackOffice.", { duration: 5000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${id}`);
    toast.success("Prospect supprimé");
    navigate("/prospects");
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    await api.post(`/prospects/${id}/comments`, { contenu: newComment, type: "commercial" });
    setNewComment("");
    loadAll();
  };

  const vendeurs = users.filter((u) => ["vendeur", "manager_commercial", "admin", "dirigeant"].includes(u.role));

  return (
    <div className="p-8" data-testid="prospect-detail-page">
      <Toaster position="top-right" richColors />
      <Link to="/prospects" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#111111] mb-4" data-testid="back-to-prospects">
        <ArrowLeft className="w-4 h-4" /> Retour aux prospects
      </Link>

      <PageHeader
        title={`${prospect.prenom} ${prospect.nom}`}
        subtitle="Fiche prospect"
        actions={
          <>
            <StatusBadge value={prospect.statut} kind="prospect" />
            <SecondaryButton
              onClick={() => window.dispatchEvent(new CustomEvent("open-assistant", { detail: { prospect_id: id, label: `Prospect ${prospect.prenom} ${prospect.nom}` } }))}
              data-testid="ask-ai-prospect-btn"
            >
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B8873A]" /> Demander à l&apos;IA</span>
            </SecondaryButton>
            {canDelete && (
              <SecondaryButton onClick={remove} data-testid="delete-prospect-button" className="!text-red-700 !border-red-300 hover:!bg-red-50">
                <span className="flex items-center gap-2"><Trash2 className="w-4 h-4" /> Supprimer</span>
              </SecondaryButton>
            )}
            {canEdit && (
              <PrimaryButton onClick={save} data-testid="save-prospect-button">
                <span className="flex items-center gap-2"><Save className="w-4 h-4" /> Enregistrer</span>
              </PrimaryButton>
            )}
          </>
        }
      />

      {prospect.statut === "signe" && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-sm flex items-center gap-3">
          <FileSignature className="w-5 h-5 text-emerald-700" />
          <div className="text-sm text-emerald-800 font-medium">
            Ce prospect est signé — un dossier a été créé dans l&apos;espace Admin / BackOffice.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Informations client">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Prénom" value={prospect.prenom || ""} onChange={(e) => set("prenom", e.target.value)} disabled={!canEdit} testid="detail-prenom" />
              <TextField label="Nom" value={prospect.nom || ""} onChange={(e) => set("nom", e.target.value)} disabled={!canEdit} testid="detail-nom" />
              <TextField label="Email" value={prospect.email || ""} onChange={(e) => set("email", e.target.value)} disabled={!canEdit} testid="detail-email" />
              <TextField label="Téléphone" value={prospect.telephone || ""} onChange={(e) => set("telephone", e.target.value)} disabled={!canEdit} testid="detail-telephone" />
              <div className="md:col-span-2">
                <TextField label="Adresse" value={prospect.adresse || ""} onChange={(e) => set("adresse", e.target.value)} disabled={!canEdit} testid="detail-adresse" />
              </div>
              <TextField label="Ville" value={prospect.ville || ""} onChange={(e) => set("ville", e.target.value)} disabled={!canEdit} testid="detail-ville" />
              <TextField label="Code postal" value={prospect.code_postal || ""} onChange={(e) => set("code_postal", e.target.value)} disabled={!canEdit} testid="detail-code-postal" />
            </div>

            <div className="px-5 pb-5">
              <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="co-acquereur-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!prospect.a_co_acquereur}
                  onChange={(e) => set("a_co_acquereur", e.target.checked)}
                  disabled={!canEdit}
                  className="w-4 h-4 accent-[#B8873A] rounded"
                  data-testid="co-acquereur-checkbox"
                />
                <span className="text-sm font-medium text-[#111111]">Il y a un co-acquéreur</span>
              </label>

              {prospect.a_co_acquereur && (
                <div className="mt-4 border border-[#B8873A]/30 bg-[#FBFAF7] rounded-sm p-4" data-testid="co-acquereur-section">
                  <div className="text-xs uppercase tracking-wider text-[#B8873A] font-bold mb-3">Co-acquéreur</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField label="Prénom" value={prospect.co_prenom || ""} onChange={(e) => set("co_prenom", e.target.value)} disabled={!canEdit} testid="co-prenom" />
                    <TextField label="Nom" value={prospect.co_nom || ""} onChange={(e) => set("co_nom", e.target.value)} disabled={!canEdit} testid="co-nom" />
                    <TextField label="Email" value={prospect.co_email || ""} onChange={(e) => set("co_email", e.target.value)} disabled={!canEdit} testid="co-email" />
                    <TextField label="Téléphone" value={prospect.co_telephone || ""} onChange={(e) => set("co_telephone", e.target.value)} disabled={!canEdit} testid="co-telephone" />
                    <div className="md:col-span-2">
                      <TextField label="Adresse (si différente)" value={prospect.co_adresse || ""} onChange={(e) => set("co_adresse", e.target.value)} disabled={!canEdit} testid="co-adresse" />
                    </div>
                    <TextField label="Ville" value={prospect.co_ville || ""} onChange={(e) => set("co_ville", e.target.value)} disabled={!canEdit} testid="co-ville" />
                    <TextField label="Code postal" value={prospect.co_code_postal || ""} onChange={(e) => set("co_code_postal", e.target.value)} disabled={!canEdit} testid="co-code-postal" />
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Programme immobilier">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Programme recherché" value={prospect.programme_recherche || ""} onChange={(e) => set("programme_recherche", e.target.value)} disabled={!canEdit} testid="detail-programme" />
              <TextField label="Lot proposé" value={prospect.lot_propose || ""} onChange={(e) => set("lot_propose", e.target.value)} disabled={!canEdit} testid="detail-lot" />
              <TextField label="Promoteur" value={prospect.promoteur || ""} onChange={(e) => set("promoteur", e.target.value)} disabled={!canEdit} testid="detail-promoteur" />
              <TextField label="Prix (€)" type="number" value={prospect.prix || ""} onChange={(e) => set("prix", e.target.value)} disabled={!canEdit} testid="detail-prix" />
              <TextField label="Budget min (€)" type="number" value={prospect.budget_min || ""} onChange={(e) => set("budget_min", e.target.value)} disabled={!canEdit} testid="detail-budget-min" />
              <TextField label="Budget max (€)" type="number" value={prospect.budget_max || ""} onChange={(e) => set("budget_max", e.target.value)} disabled={!canEdit} testid="detail-budget-max" />
            </div>
          </Section>

          <Section title="Suivi commercial">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Statut" value={prospect.statut} onChange={(e) => set("statut", e.target.value)} options={PROSPECT_STATUSES} disabled={!canEdit} testid="detail-statut" />
              <SelectField label="Niveau RDV" value={prospect.niveau_rdv || ""} onChange={(e) => set("niveau_rdv", e.target.value)} options={[{value:"",label:"—"},{value:"R0",label:"R0 (contact tél.)"},{value:"R1",label:"R1 (1er RDV)"},{value:"R2",label:"R2 (2e RDV)"},{value:"R3",label:"R3 (3e RDV)"}]} disabled={!canEdit} testid="detail-niveau-rdv" />
              <SelectField label="Vendeur assigné" value={prospect.vendeur_id || ""} onChange={(e) => set("vendeur_id", e.target.value)}
                options={[{ value: "", label: "Non assigné" }, ...vendeurs.map((v) => ({ value: v.id, label: `${v.name} (${ROLE_LABELS[v.role] || v.role})` }))]} disabled={!canEdit} testid="detail-vendeur" />
              <TextField label="Date prochain RDV" type="date" value={(prospect.date_prochain_rdv || "").slice(0,10)} onChange={(e) => set("date_prochain_rdv", e.target.value)} disabled={!canEdit} testid="detail-rdv" />
              <TextField label="Heure du RDV" type="time" value={prospect.heure_prochain_rdv || ""} onChange={(e) => set("heure_prochain_rdv", e.target.value)} disabled={!canEdit} testid="detail-rdv-heure" />
              <SelectField label="Type de RDV" value={prospect.type_rdv || ""} onChange={(e) => set("type_rdv", e.target.value)} options={[{value:"",label:"—"},{value:"Physique",label:"Physique (agence)"},{value:"Téléphone",label:"Téléphone"},{value:"Visio",label:"Visio"},{value:"Sur site",label:"Sur site / bien"}]} disabled={!canEdit} testid="detail-rdv-type" />
              <div className="flex items-end">
                {prospect.date_prochain_rdv ? (
                  <SecondaryButton onClick={downloadIcs} data-testid="rdv-ics-btn">
                    <span className="flex items-center gap-2"><CalendarPlus className="w-4 h-4" /> Ajouter à mon agenda</span>
                  </SecondaryButton>
                ) : <span className="text-xs text-slate-400">Renseigne une date pour l'ajouter à ton agenda</span>}
              </div>
              <TextField label="Date prochaine relance" type="date" value={(prospect.date_prochaine_relance || "").slice(0,10)} onChange={(e) => set("date_prochaine_relance", e.target.value)} disabled={!canEdit} testid="detail-relance" />
              <TextField label="Source" value={prospect.source || ""} onChange={(e) => set("source", e.target.value)} disabled={!canEdit} testid="detail-source" />
              <div className="md:col-span-2">
                <TextArea label="Notes internes" rows={3} value={prospect.notes || ""} onChange={(e) => set("notes", e.target.value)} disabled={!canEdit} testid="detail-notes" />
              </div>
            </div>
            {prospect.statut === "a_relancer" && (
              <div className="mx-5 mb-5 bg-[#FBFAF7] border border-[#B8873A]/30 rounded-sm p-4" data-testid="relance-cadence">
                <div className="flex items-center flex-wrap gap-3">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full bg-[#B8873A]/10 text-[#B8873A] border border-[#B8873A]/30">
                    <RefreshCw className="w-3 h-3" /> Cadence R{Math.min(prospect.relance_niveau || 1, 3)}
                  </span>
                  <span className="text-sm text-slate-600">
                    {prospect.relance_terminee
                      ? "Les 3 relances ont été effectuées."
                      : prospect.relance_prochaine_date
                        ? <>Prochaine relance prévue le <b>{formatDate(prospect.relance_prochaine_date)}</b></>
                        : "Relance à effectuer."}
                  </span>
                  {canEdit && !prospect.relance_terminee && (
                    <div className="flex gap-2 ml-auto">
                      <SecondaryButton onClick={() => doRelance("appel", false)} disabled={relancing === "appel"} data-testid="relance-appel-btn">
                        <span className="flex items-center gap-2"><Phone className="w-4 h-4" />{relancing === "appel" ? "…" : "Marquer relancé (appel)"}</span>
                      </SecondaryButton>
                      <PrimaryButton onClick={() => doRelance("email", true)} disabled={!prospect.email || relancing === "email"} data-testid="relance-email-btn">
                        <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{relancing === "email" ? "…" : `Relancer par email (R${Math.min(prospect.relance_niveau || 1, 3)})`}</span>
                      </PrimaryButton>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Section>

          <Section title="Communication client" actions={<Mail className="w-4 h-4 text-[#B8873A]" />}>
            <div className="p-5">
              <p className="text-sm text-slate-500 mb-4">
                Envoyez manuellement les emails normalement automatiques. Ils partent immédiatement
                {" "}(sans passer par la file de validation).
              </p>
              {!prospect.email ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Ce prospect n'a pas d'adresse email — renseignez-la pour pouvoir envoyer.
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <SecondaryButton
                    onClick={() => sendClientEmail("presentation")}
                    disabled={!canEdit || sendingEmail === "presentation"}
                    data-testid="send-presentation-email-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {sendingEmail === "presentation" ? "Envoi…" : "Envoyer l'email de présentation"}
                    </span>
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => sendClientEmail("remerciement")}
                    disabled={!canEdit || sendingEmail === "remerciement"}
                    data-testid="send-remerciement-email-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {sendingEmail === "remerciement" ? "Envoi…" : "Envoyer l'email de remerciement"}
                    </span>
                  </SecondaryButton>
                </div>
              )}
            </div>
          </Section>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-sm border border-slate-200">
            <div className="flex border-b border-slate-200">
              <button data-testid="tab-comments" onClick={() => setTab("comments")} className={`flex-1 px-4 py-3 text-sm font-semibold uppercase tracking-wider ${tab === "comments" ? "text-[#111111] border-b-2 border-[#B8873A]" : "text-slate-500"}`}>
                <MessageSquare className="w-4 h-4 inline mr-1" /> Commentaires
              </button>
              <button data-testid="tab-history" onClick={() => setTab("history")} className={`flex-1 px-4 py-3 text-sm font-semibold uppercase tracking-wider ${tab === "history" ? "text-[#111111] border-b-2 border-[#B8873A]" : "text-slate-500"}`}>
                <HistoryIcon className="w-4 h-4 inline mr-1" /> Historique
              </button>
            </div>
            <div className="p-4 max-h-[600px] overflow-y-auto">
              {tab === "comments" && (
                <div>
                  <form onSubmit={postComment} className="mb-4">
                    <TextArea rows={3} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Ajouter un commentaire commercial…" testid="new-comment-input" />
                    <PrimaryButton type="submit" className="mt-2 w-full" data-testid="add-comment-button">Ajouter le commentaire</PrimaryButton>
                  </form>
                  {comments.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-6">Aucun commentaire</div>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((c) => (
                        <div key={c.id} className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid={`comment-${c.id}`}>
                          <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.contenu}</div>
                          <div className="text-[11px] text-slate-500 mt-2 flex justify-between">
                            <span>{c.user_name}</span>
                            <span>{new Date(c.created_at).toLocaleString("fr-FR")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tab === "history" && (
                <div>
                  {history.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-6">Aucune activité</div>
                  ) : (
                    <div>
                      {history.map((h) => (
                        <div key={h.id} className="timeline-item" data-testid={`history-${h.id}`}>
                          <div className="text-sm font-semibold text-[#111111]">{h.action}</div>
                          {h.details && <div className="text-xs text-slate-600 mt-1">{h.details}</div>}
                          <div className="text-[11px] text-slate-500 mt-1">{h.user_name} · {new Date(h.created_at).toLocaleString("fr-FR")}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Créé le</div>
            <div className="text-sm text-slate-800">{formatDate(prospect.created_at)}</div>
            {prospect.updated_at && (
              <>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-2">Dernière MAJ</div>
                <div className="text-sm text-slate-800">{new Date(prospect.updated_at).toLocaleString("fr-FR")}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
